import { Alert, Card, Descriptions, Table, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Link, useInRouterContext } from 'react-router-dom';
import { ListenerName, validDisplayName, type ListenerNameSource } from '../../lib/listenerName';
import { adminLinks } from '../../lib/adminLinks';
import { formatDuration, formatWhen } from './callDisconnect';

/** `GET /admin/call-requests/:id` — one direct call with automatic fallback. */
export interface CallRequestDetail {
  id: string;
  mode: 'audio' | 'video';
  status: string;
  endReason: string | null;
  /** displayName = the caller's own Display Name — absent on older APIs. */
  caller: { handle: string | null; displayName?: string | null; avatarSeed: string | null } | null;
  selectedListenerId: string;
  /** Legacy combined label (Display Name, else handle). */
  selectedListenerName: string | null;
  /** The picked listener's Display Name / @handle, separately — absent on older APIs. */
  selectedListenerDisplayName?: string | null;
  selectedListenerHandle?: string | null;
  limits: { maxDistinctListeners: number; maxAttemptsPerListener: number; maxDurationSec: number };
  startedAt: string;
  expiresAt: string;
  endedAt: string | null;
  timeline: Array<{
    listenerId: string | null;
    listenerName: string | null;
    listenerDisplayName?: string | null;
    listenerHandle?: string | null;
    outcome: 'rang' | 'skipped';
    reason: string | null;
    attemptNo: number | null;
    sessionId: string | null;
    at: string | null;
    /** The ring went to a dummy (placeholder) listener. */
    dummy?: true;
  }>;
  attempts: Array<{
    sessionId: string;
    attemptNo: number | null;
    listenerId: string;
    listenerName: string | null;
    listenerDisplayName?: string | null;
    listenerHandle?: string | null;
    status: string;
    endedBy: string | null;
    endReason: string | null;
    startedAt?: string | null;
    connectedAt?: string | null;
    endedAt?: string | null;
    durationSeconds: number;
  }>;
  connected: {
    sessionId: string;
    listenerId: string;
    durationSeconds: number;
    totalChargedCoins: number;
    listenerSharePaise: string;
    endedBy: string | null;
    endReason: string | null;
  } | null;
}

const REQUEST_STATUS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Finding a buddy', color: 'processing' },
  ringing: { label: 'Ringing', color: 'processing' },
  connecting: { label: 'Connecting', color: 'processing' },
  connected: { label: 'Connected', color: 'green' },
  completed: { label: 'Completed', color: 'green' },
  cancelled: { label: 'Cancelled by caller', color: 'default' },
  expired: { label: 'Time limit reached', color: 'orange' },
  exhausted: { label: 'No buddy answered', color: 'orange' },
  failed: { label: 'Failed', color: 'red' },
};

/** One ring's outcome in plain words, from its session's end reason. */
export function ringOutcomeLabel(a: { status: string; endReason: string | null }): string {
  if (a.status === 'connected' || a.status === 'ended_normal' || a.status === 'ended_low_balance') {
    return 'Connected';
  }
  switch (a.endReason) {
    case 'declined':
      return 'Declined';
    case 'no_answer':
      return 'No answer';
    case 'cancelled':
      return 'Cancelled';
    case 'connect_timeout':
      return "Accepted, couldn't connect";
    default:
      return a.status.startsWith('ended_') ? 'Ended' : 'Ringing';
  }
}

/** Why a candidate was skipped (never rung), in plain words. */
export function skipReasonLabel(code: string | null): string {
  switch (code) {
    case 'LISTENER_OFFLINE':
      return 'Offline';
    case 'LISTENER_BUSY':
      return 'Busy on another call';
    case 'BLOCKED':
      return 'Blocked';
    case 'LISTENER_COOLDOWN':
      return 'In cooldown';
    case 'LISTENER_UNAVAILABLE':
      return 'Not available (suspended / not approved)';
    case 'CALL_PRICING_UNSET':
      return 'No price set for their tier';
    case 'LISTENER_MODE_OFF':
      return 'Has this call mode switched off';
    case 'SELF_CALL':
      return 'Is the caller';
    default:
      return code ?? 'Skipped';
  }
}

/** Why the whole request ended, in plain words. */
export function requestEndReasonLabel(code: string | null): string | null {
  switch (code) {
    case null:
      return null;
    case 'time_limit':
      return 'Ran out of time';
    case 'no_listeners':
      return 'Nobody left to try';
    case 'caller_cancelled':
      return 'Caller hung up while it was ringing';
    case 'caller_app_gone':
      // NOT a hang-up: the caller's app came back with no call screen and no
      // live call, so it stopped a request nobody was left to follow.
      return "Stopped automatically — the caller's app was no longer following it";
    case 'superseded':
      // The caller started another call while this one was still ringing with
      // no screen following it; the new call replaced it.
      return 'Replaced by a newer call from the same caller';
    case 'internal_error':
      return 'Internal error';
    default:
      return skipReasonLabel(code);
  }
}

/**
 * A rung listener for <ListenerName>: the separate Display Name + @handle when
 * the API sends them, else the legacy combined `listenerName` (older APIs).
 */
export function ringListener(
  displayName: string | null | undefined,
  handle: string | null | undefined,
  legacyName: string | null | undefined,
  id: string | null | undefined,
): ListenerNameSource {
  if (displayName !== undefined || handle) return { displayName, handle, id };
  return { displayName: legacyName, id };
}

/**
 * The fallback request a call belongs to: who the caller picked, every ring and
 * skip in order, and the listener who finally connected.
 */
export function CallRequestPanel({ requestId, currentSessionId }: { requestId: string; currentSessionId: string }) {
  const q = useQuery({
    queryKey: ['admin', 'call-request', requestId],
    queryFn: async () => (await api.get<CallRequestDetail>(`/admin/call-requests/${requestId}`)).data,
  });
  // Hooks first, before any early return.
  const inRouter = useInRouterContext();
  if (q.isError) return <Alert type="warning" showIcon message="Could not load this call's fallback request." />;
  const r = q.data;
  const ringsBySession = new Map((r?.attempts ?? []).map((a) => [a.sessionId, a]));
  const st = r ? (REQUEST_STATUS[r.status] ?? { label: r.status, color: 'default' }) : null;
  const endWhy = r ? requestEndReasonLabel(r.endReason) : null;

  return (
    <Card size="small" title="Fallback request" loading={q.isLoading} data-testid="call-request-panel">
      {r && st && (
        <>
          <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Picked by caller">
              <ListenerName
                listener={ringListener(
                  r.selectedListenerDisplayName,
                  r.selectedListenerHandle,
                  r.selectedListenerName,
                  r.selectedListenerId,
                )}
                listenerId={r.selectedListenerId}
                secondary={validDisplayName(r.selectedListenerDisplayName) && r.selectedListenerHandle ? 'below' : 'none'}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Result">
              <Tag color={st.color}>{st.label}</Tag>
              {endWhy && r.status !== 'completed' && r.status !== 'connected' ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {endWhy}
                </Typography.Text>
              ) : null}
            </Descriptions.Item>
            <Descriptions.Item label="Started">
              {formatWhen(r.startedAt)}
              {r.endedAt ? ` → ${formatWhen(r.endedAt)}` : ''}
            </Descriptions.Item>
            <Descriptions.Item label="Limits">
              {r.limits.maxDistinctListeners} buddies · {r.limits.maxAttemptsPerListener} rings each ·{' '}
              {Math.round(r.limits.maxDurationSec / 6) / 10} min
            </Descriptions.Item>
            {r.connected && (
              <Descriptions.Item label="Connected call" span={2}>
                {formatDuration(r.connected.durationSeconds)} · {r.connected.totalChargedCoins} coins charged
              </Descriptions.Item>
            )}
          </Descriptions>
          <Table
            size="small"
            rowKey="key"
            pagination={false}
            dataSource={r.timeline.map((t, i) => ({ ...t, key: String(i) }))}
            columns={[
              { title: '#', key: 'n', width: 50, render: (_: unknown, _row, i) => i + 1 },
              {
                title: 'Listener',
                key: 'who',
                render: (_: unknown, row) => (
                  <ListenerName
                    listener={ringListener(row.listenerDisplayName, row.listenerHandle, row.listenerName, row.listenerId)}
                    listenerId={row.listenerId}
                    secondary={validDisplayName(row.listenerDisplayName) && row.listenerHandle ? 'below' : 'none'}
                  />
                ),
              },
              {
                title: 'Outcome',
                key: 'outcome',
                render: (_: unknown, row) => {
                  if (row.outcome === 'skipped') {
                    return <Tag>Skipped — {skipReasonLabel(row.reason)}</Tag>;
                  }
                  const ring = row.sessionId ? ringsBySession.get(row.sessionId) : undefined;
                  const label = ring ? ringOutcomeLabel(ring) : 'Rang';
                  return (
                    <>
                      <Tag color={label === 'Connected' ? 'green' : 'default'}>
                        Ring {row.attemptNo}: {label}
                      </Tag>
                      {row.dummy && <Tag color="purple">dummy</Tag>}
                    </>
                  );
                },
              },
              {
                title: 'When',
                key: 'at',
                width: 170,
                render: (_: unknown, row) => {
                  const ring = row.sessionId ? ringsBySession.get(row.sessionId) : undefined;
                  const at = row.at ?? ring?.startedAt ?? null;
                  if (!at) return '—';
                  const rangSec =
                    ring?.startedAt && (ring.connectedAt ?? ring.endedAt)
                      ? Math.max(
                          0,
                          Math.round(
                            (new Date((ring.connectedAt ?? ring.endedAt) as string).getTime() -
                              new Date(ring.startedAt).getTime()) /
                              1000,
                          ),
                        )
                      : null;
                  return (
                    <span>
                      {new Date(at).toLocaleTimeString()}
                      {rangSec !== null && (
                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                          {' '}
                          · rang {rangSec}s
                        </Typography.Text>
                      )}
                    </span>
                  );
                },
              },
              {
                title: '',
                key: 'this',
                width: 110,
                render: (_: unknown, row) =>
                  row.sessionId === currentSessionId ? (
                    <Typography.Text type="secondary">this call</Typography.Text>
                  ) : row.sessionId && inRouter ? (
                    // The other rings of the same request are calls too: one click to each.
                    <Link to={adminLinks.call(row.sessionId)}>Open ring</Link>
                  ) : null,
              },
            ]}
          />
        </>
      )}
    </Card>
  );
}
